import { Link, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Menu, X, Instagram, Facebook, Youtube } from "lucide-react";

import { cn } from "@/lib/utils";
import { useSiteSettings } from "@/lib/use-site-settings";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/", label: "Início", exact: true },
  { to: "/quem-e", label: "Quem é", exact: false },
  { to: "/propostas", label: "Propostas", exact: false },
  { to: "/trabalhos", label: "Trabalhos", exact: false },
  { to: "/noticias", label: "Notícias", exact: false },
  { to: "/agenda", label: "Agenda", exact: false },
  { to: "/videos", label: "Vídeos", exact: false },
  { to: "/galeria", label: "Galeria", exact: false },
  { to: "/materiais", label: "Materiais", exact: false },
  { to: "/contato", label: "Contato", exact: false },
] as const;

export function SiteHeader() {
  const { candidateName, candidateNumber, logoUrl } = useSiteSettings();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navLinkClass = (isActive: boolean) =>
    cn(
      "rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
      isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary",
    );

  return (
    <>
      {/* Top bar */}
      <div className="hidden bg-primary-deep text-primary-foreground/80 md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 text-xs">
          <span className="font-semibold tracking-wide">
            {candidateName} • {candidateNumber}
          </span>
          <div className="flex items-center gap-3">
            <a
              href="https://instagram.com/rurdiney40123"
              target="_blank"
              rel="noreferrer"
              className="hover:text-primary-foreground"
              aria-label="Instagram"
            >
              <Instagram className="size-3.5" />
            </a>
            <a
              href="https://facebook.com/rurdiney40123"
              target="_blank"
              rel="noreferrer"
              className="hover:text-primary-foreground"
              aria-label="Facebook"
            >
              <Facebook className="size-3.5" />
            </a>
            <a
              href="https://youtube.com/@rurdiney40123"
              target="_blank"
              rel="noreferrer"
              className="hover:text-primary-foreground"
              aria-label="YouTube"
            >
              <Youtube className="size-3.5" />
            </a>
            <Link
              to="/admin"
              className="text-primary-foreground/50 hover:text-primary-foreground"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header
        className={cn(
          "sticky top-0 z-50 transition-all duration-300",
          scrolled
            ? "bg-background/95 shadow-soft backdrop-blur-md"
            : "bg-background",
        )}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3">
          <Link to="/" className="flex items-center gap-3" aria-label={candidateName}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`Logo ${candidateName} — Nº ${candidateNumber}`}
                className="h-12 w-auto max-w-[220px] object-contain"
              />
            ) : (
              <span className="leading-tight">
                <span className="block font-display text-lg text-foreground">
                  {candidateName}
                </span>
                <span className="block text-xs font-bold uppercase tracking-widest text-accent-deep">
                  Nº {candidateNumber}
                </span>
              </span>
            )}
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                {...(item.exact ? { activeOptions: { exact: true } } : {})}
                activeProps={{ className: "bg-primary text-primary-foreground" }}
                inactiveProps={{ className: "text-foreground hover:bg-secondary" }}
                className="rounded-lg px-3 py-2 text-sm font-semibold transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="rounded-lg p-2 text-foreground lg:hidden"
            aria-label={open ? "Fechar menu" : "Abrir menu"}
          >
            {open ? <X className="size-6" /> : <Menu className="size-6" />}
          </button>
        </div>

        {/* Mobile nav */}
        {open && (
          <nav className="border-t border-border bg-background px-4 py-3 lg:hidden">
            <div className="grid gap-1">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  {...(item.exact ? { activeOptions: { exact: true } } : {})}
                  activeProps={{ className: "bg-primary text-primary-foreground" }}
                  inactiveProps={{ className: "text-foreground hover:bg-secondary" }}
                  className="rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/admin"
                className="rounded-lg px-3 py-2.5 text-sm font-semibold text-muted-foreground hover:bg-secondary"
              >
                Painel Admin
              </Link>
            </div>
          </nav>
        )}
      </header>
    </>
  );
}

export { Toaster };
